/**
 * CAPI Routes
 *
 * The top level object has keys that are types of entities (think:
 * database tables). Under those keys are RESTful instance (key: "one") and collection routes
 * (key: "all").
 *
 * Some routes won't fit in this paradigm so put them in the most logical place. For endpoints that
 * share the same route but use different HTTP verbs, just add the route once.
 */
export default {
  projects: {
    all: () => `/projects`,
    one: projectUid => `/projects/${projectUid}`,
  },
  projectedAttachments: {
    allInWorkspace: workspaceUid => `/workspaces/${workspaceUid}/attachments`,
  },
  sheetHistorySets: {
    allInWorkspace: workspaceUid =>
      `/workspaces/${workspaceUid}/sheet_history_sets`,
  },
  attachments: {
    oneInProject: (projectUid, attachmentUid) =>
      `/projects/${projectUid}/attachments/${attachmentUid}`,
    allInProject: projectUid => `/projects/${projectUid}/attachments`,
    uploads: projectUid => `/projects/${projectUid}/attachments/uploads`,
  },
  sheets: {
    oneInProject: (projectUid, sheetUid) =>
      `/projects/${projectUid}/sheets/${sheetUid}`,
    allInProject: projectUid => `/projects/${projectUid}/sheets`,
    uploads: projectUid => `/projects/${projectUid}/sheets/uploads`,
    uploadFileToVersion: (projectUid, versionUploadUid, fileUploadRequestUid) =>
      `projects/${projectUid}/sheets/uploads/${versionUploadUid}/files/${fileUploadRequestUid}`,
    completeVersionUpload: (projectUid, versionUploadUid) =>
      `projects/${projectUid}/sheets/uploads/${versionUploadUid}/completions`,
  },
  photos: {
    oneInProject: (projectUid, photoUid) =>
      `/projects/${projectUid}/photos/${photoUid}`,
  },
  users: {
    profile: () => "/me",
    allInProject: projectUid => `/v2/projects/${projectUid}/users`,
    inviteToProject: projectUid => `/projects/${projectUid}/users/invites`,
    restrictedAssignees: projectUid =>
      `/projects/${projectUid}/restricted_users`,
  },
  groups: {
    allInProject: projectUid => `/projects/${projectUid}/groups`,
  },
  roles: {
    allInProject: projectUid => `/projects/${projectUid}/roles`,
  },
  issues: {
    oneInProject: (projectUid, issueUid) =>
      `/projects/${projectUid}/issues/${issueUid}`,
  },
  rfis: {
    allInProject: projectUid => `/projects/${projectUid}/rfis`,
  },
  rfis2: {
    allInProject: projectUid => `/projects/${projectUid}/rfis2`,
    statusesInProject: projectUid => `/projects/${projectUid}/rfis2/statuses`,
    settingsInProject: projectUid => `/projects/${projectUid}/rfis2/settings`,
  },
  uploads: {
    complete: webhookUrl => webhookUrl.slice(webhookUrl.indexOf("/projects")),
  },
  userEvents: {
    allForProject: projectUid => `/user_events?project_uid=${projectUid}`,
  },
  // prototype API routes
  proto: {
    deviceSync: {
      pageInProject: (projectUid, skip) =>
        `/prototypes/device_sync/${projectUid}_/filter?limit=50&skip=${skip}`,
      masterSetInProject: (projectUid, masterUid) =>
        `/prototypes/device_sync/${projectUid}/${masterUid}`,
    },
  },
  myEntitlements: {
    all: () => `/me/entitlements`,
    one: entitlementType => `/me/entitlements/${entitlementType}`,
  },
  projectEntitlements: {
    get: projectUid => `/projects/${projectUid}/entitlements`,
  },
  userEntitlements: {
    all: userId => `/user/${userId}/entitlements`,
    one: (userId, entitlementType) =>
      `/user/${userId}/entitlements/${entitlementType}`,
  },
  orgEntitlements: {
    all: orgId => `/organizations/${orgId}/entitlements`,
    one: (orgId, entitlementType) =>
      `/organizations/${orgId}/entitlements/${entitlementType}`,
  },
  versionSets: {
    all: projectUid => `/projects/${projectUid}/version_sets`,
  },
};
